import {
    getSetupIntent,
    listSetupIntents
} from './queries';
import {
    createSetupIntent,
    deleteSetupIntent,
    updateSetupIntent,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    CreateSetupIntent,
    DeleteSetupIntent,
    GetSetupIntent,
    ListSetupIntents,
    SetupIntent,
    SetupIntentStatus,
    SetupIntentUsage,
    UpdateSetupIntent
} from "../types.ts";
import {generateClient} from "aws-amplify/api";


const client = generateClient();

export const fetchRecords = async (filter: object, limit?: number, nextToken?: string) => {

    const response = await client.graphql<GraphQLQuery<ListSetupIntents>>({
                                                                                query: listSetupIntents,
                                                                                variables: {filter: filter, limit, nextToken},
                                                                            });
    return response.data?.listSetupIntents;
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetSetupIntent>>({
                                                                              query: getSetupIntent,
                                                                              variables: {id: id},
                                                                          });
    return response.data?.getSetupIntent;
};

/**
 * FNP-918 / FNP-996: the create payload is WHITELISTED to
 * `CreateSetupIntentInput` (§6).
 *
 * ⚠️ MONEY PATH — this NARROWS what is sent. The schema declares
 * `{customerId, description, paymentMethod, paymentMethodTypes, usage}`; there
 * is no `id` and no `clientSecret`, both of which are server-issued.
 *
 * Previously the caller (useSetupIntent) `delete`d `created` off the Formik
 * values and passed the rest through untyped as `object`, so an `id: ''` from
 * the AddFormDrawer's empty record travelled to an input that does not declare
 * it. AppSync silently ignores an undeclared input field, so that was invisible.
 *
 * The record is COPIED, never mutated (FNP-996), and undefined fields are
 * omitted rather than sent as explicit nulls.
 *
 * ⚠️ `usage` and `status` are widened to accept the plain STRING form as well
 * as the enum. The four payment-flow-tester pages — money-path test harnesses
 * for the save-card, 3DS, declined-card and subscription flows — call this with
 * a literal `usage: 'offSession'`. Narrowing the parameter to the enum would
 * force an edit to all four, which is money-path surface this compliance pass
 * is not permitted to touch. The values are identical at runtime; the enum is a
 * string enum whose members ARE 'offSession'/'onSession'.
 */
type SetupIntentCreateInput =
    Partial<Omit<SetupIntent, 'usage' | 'status'>>
    & {
        usage?: SetupIntentUsage | string,
        status?: SetupIntentStatus | string,
    };

export const add = async (record: SetupIntentCreateInput) => {

    const payload: Record<string, unknown> = {};

    const creatableFields: (keyof SetupIntentCreateInput)[] = [
        'customerId',
        'description',
        'paymentMethod',
        'paymentMethodTypes',
        'usage',
    ];

    creatableFields.forEach((field) => {
        const value = record[field];
        // An empty string is an untouched optional field on this form, not an
        // instruction to store a blank — `usage` is the only one the drawer
        // pre-fills.
        if (typeof value !== 'undefined' && value !== '') {
            payload[field] = value;
        }
    });

    return addRecord(payload);
};

const addRecord = async (payload: object) => {

    const response = await client.graphql<GraphQLQuery<CreateSetupIntent>>({
                                                                                 query: createSetupIntent,
                                                                                 variables: {input: payload},
                                                                             });

    return response.data?.createSetupIntent;
};

/**
 * FNP-918 / FNP-996: the update payload is WHITELISTED to
 * `UpdateSetupIntentInput` (§6).
 *
 * ⚠️ MONEY PATH — this NARROWS what is sent; it never widens it. The schema
 * declares exactly `{id: ID!, customerId, description, paymentMethod}`
 * (flexinet-stripe-api/src/appsync-api/src/schema/features/setupIntents.graphql),
 * and those four are what travel.
 *
 * The previous implementation DENYLISTED by `delete`-ing six known-bad keys off
 * a copy. A denylist is wrong by default: every field added to `SetupIntent`
 * later is sent to the API until someone remembers to add another `delete`. An
 * allowlist is wrong only when the schema gains a field, which is a change
 * someone is already making deliberately.
 *
 * ⚠️ `clientSecret` NEVER TRAVELS. It is the credential that authorises
 * confirming this setup intent from a browser, and it is not in the input at
 * all. Under the denylist that was true only because one `delete` line said so.
 *
 * ⚠️ `status` NEVER TRAVELS EITHER. Status moves through Stripe's own
 * confirm/cancel lifecycle; posting it back through an edit form would be a
 * client asserting a money-path state transition. Not in the input, so not
 * sendable.
 *
 * ⚠️ The record it is handed is COPIED, never mutated. The caller's object is a
 * live Formik value or a React Query cache entry — `delete record.created` on
 * one of those strips fields from the copy the table is still rendering, which
 * is FNP-996. Building a fresh object makes that structurally impossible.
 *
 * Undefined fields are OMITTED rather than sent as explicit nulls: AppSync
 * reads an explicit null as "clear this attribute", so sending
 * `description: null` for a field the operator never touched would wipe it.
 */
export const update = async (id: string, record: SetupIntent) => {

    // ⚠️ `||`, not `??`. The id argument arrives as '' from a caller that has
    // no route param (the AddFormDrawer's empty record), and `??` only falls
    // back on null/undefined — so an empty string would travel as the id of a
    // money-path update, targeting nothing. Falling back to the record's own id
    // is the correct read of "which setup intent is this".
    const payload: Record<string, unknown> = {id: id || record.id};

    const updatableFields: (keyof SetupIntent)[] = [
        'customerId',
        'description',
        'paymentMethod',
    ];

    updatableFields.forEach((field) => {
        if (typeof record[field] !== 'undefined') {
            payload[field] = record[field];
        }
    });

    const response = await client.graphql<GraphQLQuery<UpdateSetupIntent>>({
                                                                                 query: (updateSetupIntent),
                                                                                 variables: {input: payload},
                                                                             });
    return response.data?.updateSetupIntent;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteSetupIntent>>({
                                                                                 query: (deleteSetupIntent),
                                                                                 variables: {input: {id: id}},

                                                                             });
    return response.data?.deleteSetupIntent;
};
